<template>
  <teleport to="body">
    <transition name="search-modal">
      <div
        v-if="show"
        class="fixed inset-0 z-[60] flex items-start justify-center pt-[100px] sm:pt-[120px] px-3"
        @click.self="$emit('close')"
      >
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/50 backdrop-blur-sm" @click="$emit('close')"></div>

        <!-- Modal panel -->
        <div
          class="relative w-full max-w-5xl max-h-[75vh] bg-theme-card rounded-2xl shadow-2xl border border-theme-border flex flex-col overflow-hidden"
          @click.stop
        >
          <!-- Header -->
          <div class="flex items-center justify-between px-5 py-3.5 border-b border-theme-border shrink-0">
            <div class="flex items-center gap-2 min-w-0">
              <i class="pi pi-search text-primary-600 text-lg"></i>
              <h2 class="text-base sm:text-lg font-bold text-theme-text truncate">
                Search Results
                <span v-if="!isLoading && results.length" class="text-sm font-normal text-theme-muted ml-1">
                  ({{ results.length }} found)
                </span>
              </h2>
            </div>
            <button
              @click="$emit('close')"
              class="p-2 rounded-full text-theme-muted hover:text-theme-text hover:bg-theme-bg2 transition-colors"
              aria-label="Close search results"
            >
              <i class="pi pi-times text-sm"></i>
            </button>
          </div>

          <!-- Body -->
          <div class="flex-1 overflow-y-auto px-4 sm:px-5 py-4">
            <!-- Skeleton loading state -->
            <div
              v-if="isLoading"
              class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4"
            >
              <SkeletonCard v-for="i in 8" :key="`search-skeleton-${i}`" />
            </div>

            <!-- Results grid -->
            <div
              v-else-if="results.length > 0"
              class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4"
              role="list"
              aria-label="Search results"
            >
              <div
                v-for="equipment in results"
                :key="equipment.id"
                class="bg-theme-card rounded-2xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-xl hover:scale-[1.01] cursor-pointer flex flex-col h-full relative group border border-theme-border"
                role="listitem"
                :aria-label="`Equipment: ${equipment.name}`"
              >
                <!-- Badges -->
                <div v-if="equipment.is_featured || equipment.is_trending" class="absolute top-2.5 left-2.5 z-10 flex flex-col gap-1">
                  <span v-if="equipment.is_trending" class="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase rounded-full bg-primary-600 text-white shadow">
                    <i class="pi pi-bolt text-[9px]"></i> Trending
                  </span>
                  <span v-if="equipment.is_featured" class="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase rounded-full bg-neutral-900 text-secondary-500 border border-secondary-500 shadow">
                    <i class="pi pi-star-fill text-[9px]"></i> Featured
                  </span>
                </div>

                <!-- Image -->
                <div @click="goToDetail(equipment.id)" class="relative w-full aspect-[4/3] overflow-hidden bg-theme-bg2">
                  <img
                    v-if="equipment.images?.length"
                    :src="getFullImageUrl(equipment.images[0].image_url)"
                    :alt="equipment.name"
                    class="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div v-else class="absolute inset-0 flex items-center justify-center">
                    <i class="pi pi-image text-4xl text-theme-muted"></i>
                  </div>
                  <span
                    v-if="!equipment.is_available || equipment.available_quantity === 0"
                    class="absolute bottom-2 left-2 px-2 py-0.5 text-[10px] font-bold rounded-full bg-red-500/90 text-white"
                  >Unavailable</span>
                </div>

                <!-- Content -->
                <div class="px-2 pt-1.5 pb-2 sm:px-3 sm:pt-2.5 sm:pb-3 flex flex-col flex-grow gap-1 sm:gap-1.5">
                  <!-- Owner row -->
                  <div class="flex items-center gap-1 sm:gap-1.5">
                    <img
                      v-if="equipment.owner?.image_url"
                      :src="equipment.owner.image_url"
                      :alt="getOwnerName(equipment)"
                      class="w-5 h-5 sm:w-6 sm:h-6 rounded-full object-cover border border-theme-border"
                    />
                    <div v-else class="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-primary-500/20 flex items-center justify-center text-[10px] sm:text-xs font-bold text-primary-600">
                      {{ getOwnerInitial(equipment) }}
                    </div>
                    <span class="text-xs sm:text-sm text-theme-text2 truncate flex-1">{{ getOwnerName(equipment) }}</span>
                    <i v-if="equipment.owner?.is_verified" class="pi pi-verified text-xs sm:text-sm text-primary-500" title="Verified"></i>
                  </div>

                  <!-- Name -->
                  <h3 class="text-xs sm:text-sm font-semibold text-theme-text line-clamp-1 leading-snug">
                    {{ store.truncateText(equipment.name, 20) }}
                  </h3>

                  <!-- Rating + Distance -->
                  <div class="flex items-center justify-between text-[11px] sm:text-sm">
                    <div class="flex items-center gap-0.5 sm:gap-1 min-w-0">
                      <span class="text-yellow-500 hidden sm:inline">{{ renderStars(equipment.rating) }}</span>
                      <span class="text-yellow-500 sm:hidden">&#9733;</span>
                      <span class="text-theme-text2 truncate">{{ (equipment.rating || 0).toFixed(1) }} ({{ equipment.equipment_reviews?.length || 0 }})</span>
                    </div>
                    <span v-if="getDistance(equipment)" class="inline-flex items-center gap-0.5 font-semibold text-theme-text text-[10px] sm:text-sm shrink-0 ml-1">
                      <i class="pi pi-map-marker text-[9px] sm:text-xs text-primary-500"></i>{{ getDistance(equipment) }}
                    </span>
                  </div>

                  <!-- Pricing -->
                  <div class="mt-auto">
                    <div class="flex items-center gap-1 mb-1">
                      <button
                        v-for="tier in [{key:'day',label:'Day'},{key:'week',label:'Week'},{key:'month',label:'Month'}]"
                        :key="tier.key"
                        @click.stop="setActiveRate(equipment.id, tier.key)"
                        class="px-1.5 py-0.5 sm:px-2 rounded-full text-[10px] sm:text-xs font-semibold transition-colors"
                        :class="getActiveRate(equipment.id) === tier.key ? 'bg-primary-500 text-white' : 'bg-theme-bg2 text-theme-text2 hover:bg-theme-bg2/80'"
                      >{{ tier.label }}</button>
                    </div>
                    <p class="text-primary-600 font-extrabold text-sm sm:text-base leading-tight">
                      {{ formatPrice(getCurrentRate(equipment)) }}
                      <span class="text-theme-muted text-[10px] sm:text-xs font-normal">/ {{ getRatePeriod(equipment.id) }}</span>
                    </p>
                  </div>

                  <!-- CTA -->
                  <button
                    @click="goToDetail(equipment.id)"
                    class="mt-0.5 sm:mt-1 w-full py-1 sm:py-1.5 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-1 bg-primary-600 text-white hover:bg-primary-500 focus:ring-primary-600"
                    aria-label="Rent this item"
                  >View Details</button>
                </div>
              </div>
            </div>

            <!-- Empty state -->
            <div v-else class="flex flex-col items-center justify-center py-16 text-center">
              <div class="flex items-center justify-center w-16 h-16 rounded-full bg-theme-bg2 shadow mb-4">
                <i class="pi pi-search text-3xl text-theme-muted"></i>
              </div>
              <h3 class="text-lg font-semibold text-theme-text2">No results found</h3>
              <p class="mt-1 text-sm text-theme-muted max-w-xs">
                Try a different keyword or adjust your location filter.
              </p>
            </div>
          </div>

          <!-- Footer -->
          <div v-if="!isLoading && results.length > 0" class="border-t border-theme-border px-5 py-3 shrink-0 flex justify-center">
            <router-link
              to="/browse"
              class="inline-flex items-center gap-2 px-6 py-2 rounded-full bg-primary-600 hover:bg-primary-500 text-white font-semibold text-sm transition-all duration-200 shadow-sm"
              @click="$emit('close')"
            >
              Browse All Items <i class="pi pi-arrow-right text-xs"></i>
            </router-link>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { reactive } from 'vue'
import { useRouter } from 'vue-router'
import { useEquipmentsStore } from '@/store/equipments'
import SkeletonCard from './SkeletonCard.vue'
import { useCurrency } from '@/composables/useCurrency'
import { useLocation } from '@/composables/useLocation'
import { resolveImageUrl } from '@/composables/useImageUrl'

const { formatPrice } = useCurrency()
const { getDistance } = useLocation()
const getFullImageUrl = resolveImageUrl

defineProps({
  show: { type: Boolean, default: false },
  results: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
})

const emit = defineEmits(['close'])

const router = useRouter()
const store = useEquipmentsStore()

// Rate tier state
const activeRates = reactive({})
const getActiveRate = (id) => activeRates[id] || 'day'
const setActiveRate = (id, tier) => { activeRates[id] = tier }
const getCurrentRate = (eq) => {
  const tier = getActiveRate(eq.id)
  if (tier === 'week') return eq.weekly_rate ?? eq.hourly_rate * 7 * 0.9
  if (tier === 'month') return eq.monthly_rate ?? eq.hourly_rate * 30 * 0.8
  return eq.hourly_rate
}
const getRatePeriod = (id) => {
  const t = getActiveRate(id)
  return t === 'week' ? 'Week' : t === 'month' ? 'Month' : 'Day'
}

// Helpers
const getOwnerName = (eq) => {
  const o = eq.owner
  if (!o) return 'Unknown'
  if (o.first_name || o.last_name) return `${o.first_name || ''} ${o.last_name || ''}`.trim()
  if (o.company_name) return o.company_name
  return 'Unknown'
}
const getOwnerInitial = (eq) => getOwnerName(eq).charAt(0).toUpperCase()

const renderStars = (rating) => {
  const r = rating || 0
  const full = Math.floor(r)
  const half = r % 1 >= 0.5 ? 1 : 0
  return '★'.repeat(full) + (half ? '☆' : '') + '☆'.repeat(5 - full - half)
}

const goToDetail = (equipmentId) => {
  if (equipmentId) {
    emit('close')
    router.push({ name: 'equipment-details', params: { id: equipmentId } })
  }
}
</script>

<style scoped>
/* Modal transitions */
.search-modal-enter-active { transition: all 0.25s ease-out; }
.search-modal-leave-active { transition: all 0.2s ease-in; }
.search-modal-enter-from,
.search-modal-leave-to {
  opacity: 0;
}
.search-modal-enter-from .relative,
.search-modal-leave-to .relative {
  transform: translateY(-12px) scale(0.97);
}
</style>
